// kfe-meter-base.js — shared base for the three meter UIs (Brief 055).
//
// iOS REFERENCE: KFEMeterBaseViewModel — the meter VCs share a base that owns
// answer state, the affected-item parameters, and the feedback dispatch. The
// three web components (Dialogue Comprehension, Listening Stage, BrainSoaking
// Familiarity) mirror that: this base owns the modal shell CSS, the 0–100
// slider primitive, the iOS feedback screen, and the terminal
// `kfe:meter-complete` dispatch. Subclasses implement only their input phase.
//
// Two-phase flow (Function A — Dialogue / Listening):
//   1. input    — prompt + control(s) + Confirm.
//   2. feedback — the iOS pass / fail screen (KFEDialoCompreMeterVC.m:447-501,
//                 KFEListeningMeterVC.m:334-371), reusing <kfe-completion-screen>.
//                 The back / listen-again button finalizes via kfe:meter-complete.
// BrainSoaking (Function B / telemetry) skips the feedback phase entirely — iOS
// dismisses it directly (KFEBrainSoakingFamMetVC.m:261-271), so its subclass
// calls _complete() straight from Confirm.
//
// UI copy is sourced from i18n keys keyed by (meterType, isPass, direction) —
// NOT from meterRule.strings, which carries only the per-item Title (Brief 054
// confirmed plists hold no per-language feedback/button strings; iOS hardcodes
// them per view controller).

import { KFEComponent } from "../core/component.js";
import { t, getLanguage } from "../core/i18n.js";
import { computeAdjustment } from "../engines/meter-rule-engine.js";

/** @typedef {import("../data/meter-rules-loader.js").MeterRule} MeterRule */

/** Shared modal-shell + control CSS. iOS presents meters full-screen modally. */
export const METER_MODAL_STYLES = `
  :host {
    position: fixed; inset: 0; z-index: 1000;
    display: flex; align-items: center; justify-content: center;
    background: rgba(0, 0, 0, 0.6);
    padding: 1rem; box-sizing: border-box;
  }
  .card {
    background: var(--kfe-surface); color: var(--kfe-fg);
    border: 1px solid var(--kfe-border);
    border-radius: var(--kfe-radius, 8px);
    width: 100%; max-width: 560px; max-height: 90vh; overflow: auto;
    padding: 1.5rem; box-sizing: border-box;
  }
  h2 { margin: 0 0 1rem; font-size: var(--kfe-h2, 1.25rem); color: var(--kfe-muted); }
  .prompt { font-size: 1.05rem; font-weight: 600; line-height: 1.4; margin: 1rem 0 0.5rem; }
  .hint { color: var(--kfe-muted); font-size: 0.85rem; margin: 0 0 0.5rem; }
  .slider-row { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1rem; }
  input[type="range"] { flex: 1; accent-color: var(--kfe-accent); }
  .slider-value { min-width: 3.5rem; text-align: right; font-variant-numeric: tabular-nums; }
  .options { list-style: none; padding: 0; margin: 0.5rem 0 1rem;
             display: flex; flex-direction: column; gap: 0.5rem; }
  .opt {
    display: block; width: 100%; text-align: left;
    padding: 0.75rem 1rem; border-radius: 8px;
    background: var(--kfe-bg); color: var(--kfe-fg);
    border: 1px solid var(--kfe-border);
    cursor: pointer; font-size: 1rem; line-height: 1.4;
  }
  .opt[aria-checked="true"] {
    background: var(--kfe-accent); color: var(--kfe-on-accent); border-color: var(--kfe-accent);
  }
  .warning { color: var(--kfe-accent); font-size: 0.9rem; margin: 0 0 0.75rem; min-height: 1.2em; }
  .actions { display: flex; justify-content: flex-end; margin-top: 0.5rem; }
  button.confirm {
    padding: 0.6rem 1.25rem; border-radius: 8px; border: 0;
    background: var(--kfe-accent); color: var(--kfe-on-accent);
    font-size: 1rem; cursor: pointer;
  }
  button.confirm[disabled] { opacity: 0.5; cursor: default; }
`;

/** @abstract */
export class KFEMeterBase extends KFEComponent {
  static props = ["meterRule"];

  /** @returns {MeterRule | null} */
  get rule() {
    return /** @type {any} */ (this).meterRule ?? null;
  }

  /** Per-item title — the ONLY per-item localized field (meterRule.strings). */
  _title() {
    const rule = this.rule;
    const s = rule && rule.strings;
    if (!s) return t("meter.title");
    const lang = getLanguage();
    const zh = s.zh && s.zh.title;
    const en = s.en && s.en.title;
    if (lang === "zh-Hans" && zh) return zh;
    return en || zh || t("meter.title");
  }

  /**
   * Build a labelled 0–100 slider. The readout updates in place; the committed
   * value is pushed to `onInput(value)` so Confirm reads instance state without
   * a re-render (mirrors the iOS continuous=NO slider commit).
   *
   * @param {string} promptText
   * @param {string} hintText
   * @param {number} value
   * @param {(v: number) => void} onInput
   * @returns {HTMLElement}
   */
  _sliderBlock(promptText, hintText, value, onInput) {
    const wrap = document.createElement("div");

    const p = document.createElement("p");
    p.className = "prompt";
    p.textContent = promptText;
    wrap.appendChild(p);

    const hint = document.createElement("p");
    hint.className = "hint";
    hint.textContent = hintText;
    wrap.appendChild(hint);

    const row = document.createElement("div");
    row.className = "slider-row";

    const input = document.createElement("input");
    input.type = "range";
    input.min = "0";
    input.max = "100";
    input.step = "1";
    input.value = String(value);
    input.setAttribute("aria-label", promptText);

    const readout = document.createElement("span");
    readout.className = "slider-value";
    readout.textContent = `${value}%`;

    input.addEventListener("input", (e) => {
      const v = Number(/** @type {HTMLInputElement} */ (e.target).value);
      readout.textContent = `${v}%`;
      onInput(v);
    });

    row.append(input, readout);
    wrap.appendChild(row);
    return wrap;
  }

  /**
   * Terminal dispatch — the host applies the result and navigates. `goToItem`
   * carries the iOS-canon navigation intent: true means the learner tapped the
   * "Listen to … again" feedback button, which on iOS navigates to the affected
   * item (kMeterGoToItemNotification) rather than returning to the lesson list
   * (kMeterBackToListNotification). The host (_onMeterComplete) reads it to pick
   * navigate(/exercise/affectedItemId) vs history.back().
   *
   * @param {number} answer1
   * @param {number | null} [answer2]
   * @param {boolean} [goToItem=false]
   */
  _complete(answer1, answer2, goToItem = false) {
    this.dispatchEvent(new CustomEvent("kfe:meter-complete", {
      detail: { answer1, answer2: answer2 ?? null, goToItem: goToItem === true },
      bubbles: true,
      composed: true,
    }));
  }

  /**
   * Build the iOS feedback screen for a Function-A meter (Dialogue / Listening),
   * mirroring KFEMeterFeedbackBaseVC + the per-VC button blocks
   * (KFEListeningMeterVC.m:110-147, KFEDialoCompreMeterVC.m:236-277):
   *   • pass                  → ONE button "Got it, go back to list" → back.
   *   • fail + preceding      → Button1 "Listen … again." → GO TO the affected
   *                             item (replay it toward the bumped playcount);
   *                             Button2 "Got it, go back to list." → back.
   *   • fail + following      → ONE button "Got it, go back to list" → back
   *                             (iOS shows no "listen again" here — the affected
   *                             song is ahead, not behind; KFEDialoCompreMeterVC.m
   *                             :483-501 sets only Button1 = back-to-list).
   * Function-B (null affectedItemId) shows a generic telemetry acknowledgement.
   *
   * Listening is always preceding (iOS hardcodes isPreceding=YES,
   * KFEListeningMeterVC.m:303; every corpus listening rule carries direction 0),
   * so it never hits the following branch.
   *
   * @param {"dialogComp"|"listening"} typeKey
   * @param {number} answer1
   * @param {number | null} answer2
   * @returns {HTMLElement}
   */
  _feedbackNode(typeKey, answer1, answer2) {
    const rule = this.rule;
    const screen = /** @type {any} */ (document.createElement("kfe-completion-screen"));
    screen.message = t("meter.feedbackTitle");
    screen.layout = "column";

    const back = () => this._complete(answer1, answer2, false);
    const goToItem = () => this._complete(answer1, answer2, true);

    if (!rule || rule.affectedItemId == null) {
      // Function B — telemetry only, no playcount message.
      screen.prompt = t("meter.complete");
      screen.actions = [{ label: t(`meter.${typeKey}.btn_back`), eventName: "kfe:meter:back", primary: true }];
      screen.addEventListener("kfe:meter:back", back);
      return screen;
    }

    const delta = computeAdjustment(rule, answer1, answer2 ?? undefined);
    const dir = rule.affectedItemDirection;
    const isFollowing = dir === 1 || dir === 3;

    if (delta <= 0) {
      // Pass — above the (latter) threshold.
      screen.prompt = t(`meter.${typeKey}.feedback_pass`);
      screen.actions = [{ label: t(`meter.${typeKey}.btn_back`), eventName: "kfe:meter:back", primary: true }];
      screen.addEventListener("kfe:meter:back", back);
      return screen;
    }

    // Fail — direction-branched copy with {delta} interpolation.
    const failKey = isFollowing ? "feedback_fail_following" : "feedback_fail_preceding";
    screen.prompt = t(`meter.${typeKey}.${failKey}`, { delta: String(delta) });

    if (isFollowing) {
      // Affected song is ahead of the meter — iOS offers only "go back to list".
      screen.actions = [{ label: t(`meter.${typeKey}.btn_back`), eventName: "kfe:meter:back", primary: true }];
      screen.addEventListener("kfe:meter:back", back);
      return screen;
    }

    // Fail + preceding — Button1 replays the affected item, Button2 backs out.
    screen.actions = [
      { label: t(`meter.${typeKey}.btn_listenAgain`), eventName: "kfe:meter:again", primary: true },
      { label: t(`meter.${typeKey}.btn_back`), eventName: "kfe:meter:back" },
    ];
    screen.addEventListener("kfe:meter:again", goToItem);
    screen.addEventListener("kfe:meter:back", back);
    return screen;
  }
}
